<template>
	<div class="new-settings-page">
		<div class="inbox-title sticky top-0 z-50 settings-title-container">
			<h1 class="inbox-title-label">
				{{ partnerName }}
			</h1>
		</div>
		<div class="new-settings">
			<!-- Secondary settings navigation -->
			<aside
				class="new-settings__sidebar"
				aria-label="Settings navigation"
			>
				<div class="new-settings__sidebar-header">
					<font-awesome-icon
						class="new-settings__sidebar-header-icon"
						fixed-width
						:icon="['fal', 'cog']"
						aria-hidden="true"
					/>
					<span class="new-settings__sidebar-header-label"
						>Settings</span
					>
				</div>

				<nav class="new-settings__nav" role="navigation">
					<button
						v-for="item in visibleNavItems"
						:key="item.id"
						type="button"
						class="new-settings__nav-item"
						:class="{
							'new-settings__nav-item--active':
								activeSectionId === item.id,
						}"
						:aria-current="
							activeSectionId === item.id ? 'page' : false
						"
						@click="selectSection(item.id)"
					>
						{{ item.label }}
					</button>
				</nav>
			</aside>

			<!-- Active settings section -->
			<main class="new-settings__content">
				<keep-alive :include="partnerFormKeepAliveNames">
					<component
						:is="activeSectionComponent"
						:key="activeSectionId"
						:organization-name="partnerName"
					/>
				</keep-alive>
			</main>
		</div>
	</div>
</template>

<script>
import _get from 'lodash/get'
import { mapActions, mapGetters } from 'vuex'
import OrganizationSection from '@/views/Settings/sections/OrganizationSection.vue'
import GeneralInformationSection from '@/views/Settings/sections/GeneralInformationSection.vue'
import OfficeHoursSection from '@/views/Settings/sections/OfficeHoursSection.vue'
import SecondaryVoicemailSection from '@/views/Settings/sections/SecondaryVoicemailSection.vue'
import EhrConfigurationSection from '@/views/Settings/sections/EhrConfigurationSection.vue'
import { invalidateEhrNoteTypesCache } from '@/views/Settings/sections/EhrConfigurationSection.vue'

/**
 * Settings section identifiers used for sidebar navigation.
 *
 * @type {Object}
 */
const SECTION_IDS = {
	ORGANIZATION: 'organization',
	GENERAL_INFORMATION: 'generalInformation',
	OFFICE_HOURS: 'officeHours',
	SECONDARY_VOICEMAIL: 'secondaryVoicemail',
	EHR_CONFIGURATION: 'ehrConfiguration',
}

/**
 * Map section ids to their view components.
 *
 * @type {Object}
 */
const SECTION_COMPONENTS = {
	[SECTION_IDS.ORGANIZATION]: OrganizationSection,
	[SECTION_IDS.GENERAL_INFORMATION]: GeneralInformationSection,
	[SECTION_IDS.OFFICE_HOURS]: OfficeHoursSection,
	[SECTION_IDS.SECONDARY_VOICEMAIL]: SecondaryVoicemailSection,
	[SECTION_IDS.EHR_CONFIGURATION]: EhrConfigurationSection,
}

/** Sections that share one partner fetch and stay mounted while navigating. */
const PARTNER_FORM_KEEP_ALIVE_NAMES = [
	'GeneralInformationSection',
	'SecondaryVoicemailSection',
	'EhrConfigurationSection',
]

export default {
	/**
	 * Allow child sections to switch sidebar navigation.
	 *
	 * @return {Object}
	 */
	provide() {
		return {
			selectSettingsSection: this.selectSection,
			ensureSettingsPartnerLoaded: this.ensureSettingsPartnerLoaded,
			invalidateSettingsPartnerCache: this.invalidateSettingsPartnerCache,
		}
	},

	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		OrganizationSection,
		GeneralInformationSection,
		OfficeHoursSection,
		SecondaryVoicemailSection,
		EhrConfigurationSection,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		/**
		 * Sidebar items filtered by partner capabilities and user role.
		 *
		 * @return {Array}
		 */
		visibleNavItems() {
			if (this.isOrgAdmin && !this.clarusAppType && !this.isEngageApp) {
				return this.navItems.filter(
					item => item.id === SECTION_IDS.ORGANIZATION
				)
			}
			return this.navItems.filter(item => {
				if (item.id !== SECTION_IDS.EHR_CONFIGURATION) {
					return true
				}

				return (
					this.isEhrConfigured &&
					(this.isAdmin || this.isPartnerAdmin)
				)
			})
		},

		/**
		 * Vue component rendered in the main content area.
		 *
		 * @return {Object}
		 */
		activeSectionComponent() {
			return SECTION_COMPONENTS[this.activeSectionId]
		},

		/**
		 * Component names cached by keep-alive for partner form sections.
		 *
		 * @return {Array}
		 */
		partnerFormKeepAliveNames() {
			return PARTNER_FORM_KEEP_ALIVE_NAMES
		},

		/**
		 * Organization display name from the active partner.
		 *
		 * @return {String}
		 */
		partnerName() {
			return this.partner?.name || ''
		},

		/**
		 * Whether the active partner has EHR settings configured.
		 *
		 * @return {Boolean}
		 */
		isEhrConfigured() {
			return Boolean(_get(this.$store.getters, 'partner.ehr_settings'))
		},

		...mapGetters({
			isAdmin: 'auth/isAdmin',
			isPartnerAdmin: 'auth/isPartnerAdmin',
			isOrgAdmin: 'auth/isOrgAdmin',
			partner: 'partners/active',
			clarusAppType: 'partners/clarusAppType',
			isEngageApp: 'partners/isEngageApp',
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Switch the visible settings section when a sidebar item is selected.
		 *
		 * @param {String} sectionId
		 * @return {void}
		 */
		selectSection(sectionId) {
			if (!this.isSectionNavigable(sectionId)) {
				return
			}

			if (this.activeSectionId === sectionId) {
				this.syncSectionQuery(sectionId)
				return
			}

			this.activeSectionId = sectionId
			this.syncSectionQuery(sectionId)
		},

		/**
		 * Whether a section id exists in settings navigation.
		 *
		 * @param {String} sectionId
		 * @return {Boolean}
		 */
		isSectionNavigable(sectionId) {
			return this.navItems.some(item => item.id === sectionId)
		},

		/**
		 * Open the section from ?section= query (e.g. navbar office status).
		 *
		 * @return {void}
		 */
		applySectionFromRoute() {
			const sectionId = this.$route.query.section

			if (!sectionId || !this.isSectionNavigable(sectionId)) {
				return
			}

			if (
				sectionId === SECTION_IDS.EHR_CONFIGURATION &&
				!this.visibleNavItems.some(
					item => item.id === SECTION_IDS.EHR_CONFIGURATION
				)
			) {
				return
			}

			this.activeSectionId = sectionId
		},

		/**
		 * Keep the URL in sync with the active settings section.
		 *
		 * @param {String} sectionId
		 * @return {void}
		 */
		syncSectionQuery(sectionId) {
			if (this.$route.name !== 'app.settings') {
				return
			}

			if (this.$route.query.section === sectionId) {
				return
			}

			this.$router
				.replace({
					name: 'app.settings',
					params: this.$route.params,
					query: { ...this.$route.query, section: sectionId },
				})
				.catch(() => {})
		},

		/**
		 * Clear cached partner load state (e.g. when route partner changes).
		 *
		 * @return {void}
		 */
		invalidateSettingsPartnerCache() {
			this.settingsPartnerLoadedForId = null
			this.settingsPartnerLoadPromise = null
			invalidateEhrNoteTypesCache()
		},

		/**
		 * Load partner once per partner id; dedupe concurrent requests.
		 *
		 * @param {Boolean} [force=false]
		 * @return {Promise<void>}
		 */
		ensureSettingsPartnerLoaded(force = false) {
			const partnerId = parseInt(this.$route.params.partner, 10)

			if (!partnerId) {
				return Promise.resolve()
			}

			if (!force && this.settingsPartnerLoadedForId === partnerId) {
				return Promise.resolve()
			}

			if (!force && this.settingsPartnerLoadPromise) {
				return this.settingsPartnerLoadPromise
			}

			this.settingsPartnerLoadPromise = this.getPartner(partnerId)
				.then(() => {
					this.settingsPartnerLoadedForId = partnerId
				})
				.finally(() => {
					this.settingsPartnerLoadPromise = null
				})

			return this.settingsPartnerLoadPromise
		},

		...mapActions({
			getPartner: 'partners/find',
		}),
	},

	name: 'NewSetting',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		'$route.params.partner'(newId, oldId) {
			if (newId !== oldId) {
				this.invalidateSettingsPartnerCache()
				this.ensureSettingsPartnerLoaded()
			}
		},

		'$route.query.section'() {
			this.applySectionFromRoute()
		},

		/**
		 * Ensure the active section remains valid when nav items change.
		 *
		 * @return {void}
		 */
		visibleNavItems: {
			immediate: true,
			handler(items) {
				const isActiveVisible = items.some(
					item => item.id === this.activeSectionId
				)

				if (!isActiveVisible && items.length > 0) {
					this.activeSectionId = items[0].id
				}

				this.applySectionFromRoute()
			},
		},
	},

	async mounted() {
		await this.ensureSettingsPartnerLoaded()
		this.applySectionFromRoute()
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			activeSectionId: SECTION_IDS.ORGANIZATION,
			settingsPartnerLoadedForId: null,
			settingsPartnerLoadPromise: null,
			navItems: [
				{
					id: SECTION_IDS.GENERAL_INFORMATION,
					label: 'General Information',
				},

				{
					id: SECTION_IDS.OFFICE_HOURS,
					label: 'Office Hours',
				},

				{
					id: SECTION_IDS.SECONDARY_VOICEMAIL,
					label: 'Secondary Voicemail',
				},
				{
					id: SECTION_IDS.EHR_CONFIGURATION,
					label: 'EHR Configuration',
				},
			],
		}
	},
}
</script>
